// Desktop "Pood" mega menu (snippets/mega-menu-shop.liquid).
// Hovering or focusing a category shows its subcategories in the next column. A pointer
// travelling diagonally towards that column crosses other categories on the way, so a
// switch waits while the pointer is still heading for the open panel.
if (!customElements.get('mega-menu-shop')) {
  customElements.define('mega-menu-shop', class MegaMenuShop extends HTMLElement {
    static DELAY = 300;
    static TOLERANCE = 75;

    connectedCallback() {
      this.categories = [...this.querySelectorAll('.mms_cat')];
      if (!this.categories.length) return;

      this.area = this.querySelector('.mms_cats');
      this.list = this.querySelector('.mms_cats-list');
      this.dropdown = this.closest('dropdown-component');
      this.opener = this.dropdown && this.dropdown.querySelector(':scope > .dropdown-component_opener');
      this.active = this.categories.find(category => category.classList.contains('is-active')) || this.categories[0];
      this.points = [];
      this.timer = null;
      this.lastDelayPoint = null;

      this.list.addEventListener('mousemove', event => {
        this.points.push({ x: event.clientX, y: event.clientY });
        if (this.points.length > 3) this.points.shift();
      });
      this.list.addEventListener('mouseleave', () => {
        clearTimeout(this.timer);
        this.points = [];
        this.lastDelayPoint = null;
      });

      this.categories.forEach(category => {
        const link = category.querySelector('.mms_cat-link');
        const panel = category.querySelector('.mms_panel');
        link.addEventListener('mouseenter', () => this.possiblyActivate(category));
        link.addEventListener('focus', () => this.activate(category));
        // The pointer reached the open panel, so a switch queued on the way there is dropped.
        if (panel) panel.addEventListener('mouseenter', () => clearTimeout(this.timer));
      });

      this.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.opener) {
        // Arrow down on "Pood" opens the menu for keyboard users; Enter still follows the link.
        this.opener.addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown' || window.innerWidth < 992) return;
          event.preventDefault();
          this.dropdown.open();
          // The panel is still hidden until its fade-in starts, and hidden links can't take focus.
          const first = this.querySelector('a[href]');
          if (first) setTimeout(() => first.focus(), 50);
        });

        // Every time the menu opens it starts from the first category.
        new MutationObserver(records => {
          if (this.opener.getAttribute('aria-expanded') !== 'true' || !records.some(record => record.oldValue !== 'true')) return;
          this.points = [];
          this.lastDelayPoint = null;
          this.activate(this.categories[0]);
          this.fitPanels();
          document.querySelector('.page-header_search-bar search-form')?.closeResults();
        }).observe(this.opener, { attributes: true, attributeFilter: ['aria-expanded'], attributeOldValue: true });
      }

      if (this.dropdown) {
        // Tabbing out of the menu closes it, the same way the pointer leaving it does.
        this.dropdown.addEventListener('focusout', event => {
          if (event.relatedTarget && !this.dropdown.contains(event.relatedTarget)) this.dropdown.close();
        });
      }
    }

    activate(category) {
      clearTimeout(this.timer);
      if (category === this.active) return;
      this.active.classList.remove('is-active');
      category.classList.add('is-active');
      this.active = category;
    }

    // Panels sit beside the list, outside the flow, so the column is sized to the tallest one.
    // Liquid sets a row-count estimate for the first paint; this replaces it with the real height.
    // Hidden panels still have a layout, so all of them can be measured at once.
    fitPanels() {
      const tallest = Math.max(...this.categories.map(category => {
        const panel = category.querySelector('.mms_panel');
        return panel ? panel.offsetHeight : 0;
      }));
      // offsetTop counts the column heading above the list (.mms_cats is the offset parent).
      this.area.style.minHeight = `${Math.max(tallest, this.list.offsetTop + this.list.offsetHeight)}px`;
    }

    possiblyActivate(category) {
      clearTimeout(this.timer);
      const delay = this.activationDelay();
      if (delay) {
        this.timer = setTimeout(() => this.possiblyActivate(category), delay);
        return;
      }
      this.activate(category);
    }

    // Menu aim: while the pointer moves inside the triangle between its previous position and
    // the open panel's left edge, it is on its way to the panel, so the switch is held back.
    activationDelay() {
      const panel = this.active.querySelector('.mms_panel');
      if (!panel || this.points.length < 2) return 0;

      const current = this.points[this.points.length - 1];
      const previous = this.points[0];
      if (this.lastDelayPoint && current.x === this.lastDelayPoint.x && current.y === this.lastDelayPoint.y) return 0;

      const rect = panel.getBoundingClientRect();
      const listRect = this.list.getBoundingClientRect();
      const upper = { x: rect.left, y: Math.min(rect.top, listRect.top) - MegaMenuShop.TOLERANCE };
      const lower = { x: rect.left, y: Math.max(rect.bottom, listRect.bottom) + MegaMenuShop.TOLERANCE };
      const slope = (a, b) => (b.y - a.y) / (b.x - a.x);

      if (slope(current, upper) < slope(previous, upper) && slope(current, lower) > slope(previous, lower)) {
        this.lastDelayPoint = current;
        return MegaMenuShop.DELAY;
      }
      this.lastDelayPoint = null;
      return 0;
    }

    // Up and down move between categories, right enters the open panel, left goes back.
    onKeydown(event) {
      const categoryLink = event.target.closest('.mms_cat-link');
      const panel = event.target.closest('.mms_panel');
      const index = this.categories.indexOf(event.target.closest('.mms_cat'));
      let target = null;

      if (categoryLink && event.key === 'ArrowDown') {
        target = this.categories[Math.min(index + 1, this.categories.length - 1)].querySelector('.mms_cat-link');
      } else if (categoryLink && event.key === 'ArrowUp') {
        target = this.categories[Math.max(index - 1, 0)].querySelector('.mms_cat-link');
      } else if (categoryLink && event.key === 'ArrowRight') {
        target = this.active.querySelector('.mms_panel a[href]');
      } else if (panel && event.key === 'ArrowLeft') {
        target = this.active.querySelector('.mms_cat-link');
      }

      if (!target) return;
      event.preventDefault();
      target.focus();
    }
  });
}
